/**
 * Capa de guardado desacoplada (punto 31 del brief).
 * Ahora sobre localStorage; preparada para migrar a IndexedDB/Supabase
 * sin tocar el resto del juego (misma API get/set/patch).
 *
 * Además puede LEER el guardado del juego clásico ("aventuras-epi-save-v2")
 * para migrar el progreso del jugador cuando toque (no lo sobrescribe).
 */

export const SAVE_KEY = "aventuras-epi-game-v1";
export const LEGACY_SAVE_KEY = "aventuras-epi-save-v2";

export interface GameSave {
  version: number;
  location: string;
  player: { x: number; y: number };
  /** Estado por misión (se rellenará al portar los sistemas). */
  missions: Record<string, unknown>;
  inventory: string[];
  equipment: Record<string, string | null>;
  flags: Record<string, boolean>;
  settings: Record<string, unknown>;
}

function defaultSave(): GameSave {
  return {
    version: 1,
    location: "boot",
    player: { x: 4, y: 4 },
    missions: {},
    inventory: [],
    equipment: {},
    flags: {},
    settings: {},
  };
}

class SaveManagerImpl {
  private data: GameSave;

  constructor() {
    this.data = this.load() ?? defaultSave();
  }

  get(): GameSave {
    return this.data;
  }

  patch(partial: Partial<GameSave>): void {
    this.data = { ...this.data, ...partial };
    this.save();
  }

  save(): void {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch {
      /* almacenamiento no disponible: el juego sigue en memoria */
    }
  }

  private load(): GameSave | null {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      return raw ? (JSON.parse(raw) as GameSave) : null;
    } catch {
      return null;
    }
  }

  /** Devuelve el guardado del juego clásico si existe (solo lectura). */
  readLegacy(): unknown | null {
    try {
      const raw = localStorage.getItem(LEGACY_SAVE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  reset(): void {
    this.data = defaultSave();
    this.save();
  }
}

export const SaveManager = new SaveManagerImpl();
